import { useEffect, useRef, useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../context/AuthContext";

type EventType =
  | "producao.created"
  | "producao.updated"
  | "producao.finalized"
  | "producao.deleted"
  | "entrega.created"
  | "entrega.updated"
  | "entrega.finalized"
  | "entrega.deleted"
  | "filaEntrega.created"
  | "filaEntrega.updated"
  | "forno.created"
  | "forno.finalized"
  | "filaIntegracao.updated"
  | "fichaCronometro.updated";

export const useRankingEvents = (
  type: "producao" | "entrega" | "ficha-diaria",
  lojaId: number | null | undefined,
  options: {
    queryKeys: readonly string[];
    onAvailabilityChange?: (available: boolean) => void;
    onEvent?: () => void;
    enabled?: boolean;
  }
) => {
  const queryClient = useQueryClient();
  const { token } = useAuth();
  const eventSourceRef = useRef<EventSource | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const reconnectAttempts = useRef(0);
  const maxReconnectAttempts = 20;
  const reconnectDelay = 3000;
  const onAvailabilityChangeRef = useRef(options.onAvailabilityChange);
  onAvailabilityChangeRef.current = options.onAvailabilityChange;
  const onEventRef = useRef(options.onEvent);
  onEventRef.current = options.onEvent;
  const enabledRef = useRef(options.enabled !== false);
  enabledRef.current = options.enabled !== false;
  const queryKeysRef = useRef(options.queryKeys);
  queryKeysRef.current = options.queryKeys;

  const notifyAvailability = useCallback((available: boolean) => {
    onAvailabilityChangeRef.current?.(available);
  }, []);

  const invalidateQueries = useCallback(() => {
    for (const key of queryKeysRef.current) {
      queryClient.invalidateQueries({
        queryKey: [key],
        refetchType: "active",
      });
    }
    onEventRef.current?.();
  }, [queryClient]);

  const connect = useCallback(() => {
    if (!token || !enabledRef.current) return;

    if (eventSourceRef.current) {
      eventSourceRef.current.close();
    }

    const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:3001";
    const params = new URLSearchParams();
    if (lojaId) {
      params.append("lojaId", lojaId.toString());
    }
    params.append("token", token);
    const url = `${apiUrl}/events/ranking/${type}?${params.toString()}`;

    const eventSource = new EventSource(url, {
      withCredentials: false,
    });

    eventSource.onopen = () => {
      reconnectAttempts.current = 0;
      notifyAvailability(true);
    };

    eventSource.addEventListener("connected", () => {
      reconnectAttempts.current = 0;
      notifyAvailability(true);
      invalidateQueries();
    });

    const eventTypes: EventType[] =
      type === "producao"
        ? [
            "producao.created",
            "producao.updated",
            "producao.finalized",
            "producao.deleted",
          ]
        : type === "ficha-diaria"
          ? ["fichaCronometro.updated"]
          : [
              "entrega.created",
              "entrega.updated",
              "entrega.finalized",
              "entrega.deleted",
              "filaEntrega.created",
              "filaEntrega.updated",
              "forno.created",
              "forno.finalized",
              "filaIntegracao.updated",
            ];

    eventTypes.forEach((eventType) => {
      eventSource.addEventListener(eventType, () => {
        invalidateQueries();
      });
    });

    eventSource.onerror = (error) => {
      console.error(`SSE error for ${type}:`, error);
      eventSource.close();

      if (reconnectAttempts.current < maxReconnectAttempts) {
        reconnectAttempts.current += 1;
        reconnectTimeoutRef.current = setTimeout(() => {
          connect();
        }, reconnectDelay * reconnectAttempts.current);
      } else {
        console.error(`Max reconnection attempts reached for ${type}`);
        notifyAvailability(false);
      }
    };

    eventSourceRef.current = eventSource;
  }, [token, type, lojaId, invalidateQueries, notifyAvailability]);

  const enabled = options.enabled !== false;

  useEffect(() => {
    if (!enabled) {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      return;
    }

    connect();

    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
    };
  }, [connect, enabled]);

  useEffect(() => {
    if (!enabled) return;
    const onVisible = () => {
      if (document.visibilityState !== "visible") return;
      reconnectAttempts.current = 0;
      const current = eventSourceRef.current;
      if (!current || current.readyState === EventSource.CLOSED) {
        connect();
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("pageshow", onVisible);
    window.addEventListener("focus", onVisible);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("pageshow", onVisible);
      window.removeEventListener("focus", onVisible);
    };
  }, [connect, enabled]);

  return {
    reconnect: connect,
  };
};
